import { MatchingStrategies } from 'meilisearch';

const DEFAULT_SEARCH_DISTANCE_IN_METERS = 100000;
const DEFAULT_VISIBLE_LIST_ITEMS = 15;
const MARKET_PLACE_VISIBLE_ITEMS = 16;
const TOP_PRODUCTS_DEFAULT_VISIBLE_ITEMS = 9;
const BLACK_BOARD_VISIBLE = false;
const MINIMUM_PRICE = 0.99;

// Fallback vendor whose news/jobs seed the blackboard when a visitor has no
// favourites (or isn't logged in), so the board is never empty.
const DEFAULT_FAVORITE_VENDOR_ID = 4129;

// Deploy-time feature flag: enables the whole webshop (basket, checkout, Stripe
// payments, orders, payouts, shipping, vendor subscriptions / selling plans).
// Default OFF — only enabled when REACT_APP_WEBSHOP_ENABLED is exactly 'true'.
const WEBSHOP_ENABLED = process.env.REACT_APP_WEBSHOP_ENABLED === 'true';

// Public base URL that shareable links are built from. Falls back to the origin
// the app is served from, so a share from dev/test points at that environment
// instead of handing out a production link that may not exist there.
const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;

// The WKO allergen chart linked from every gastro menu.
const ALLERGEN_INFO_URL = 'https://api.region-im-netz.com/assets/WKO_ALLERGENE_PLAKAT-A3_OOe.PDF';

const DEFAULT_MEILICLIENT_LIMIT = 1000;

// Hard cap on the title field of user/vendor-created assets (products, events,
// jobs, news, posts, services, gastro menus, shipping rates). Enforced as a
// maxLength on the inputs so long titles cannot break card and listing layouts.
const MAX_ASSET_TITLE_LENGTH = 55;

// The Schwerpunkt of a shop and of a Dienstleistung; both backend schemas have
// the same `maxLength`.
const FOCUS_AREA_MAX_LENGTH = 80;

// MeiliSearch predicate for public listings: hide content belonging to a vendor
// who has deactivated their account.
//
// `!= true` rather than `= false` on purpose — an equality filter matches only
// documents that actually carry the field, so `= false` would silently drop
// user-created content that has no vendor at all (region events, for example).
const NOT_SUSPENDED_VENDOR_FILTER = 'vendor.suspended != true';

// Companion predicate for content whose AUTHOR has deactivated their account.
// Same `!= true` reasoning as above: it must also match documents indexed
// before the field existed, which carry no value for it at all.
const NOT_DEACTIVATED_OWNER_FILTER = 'ownerDeactivated != true';

// Predicate for the Termine tab: only events that somebody on the platform is
// putting on — a vendor or a user. Events the admin enters for a third party
// (no vendor, no user, organiser typed into `customVendorTitle`: the weekly
// markets brought over from SilverStripe) stay calendar-only, as they were on
// the legacy site, where the Events tab never read the RegionEvent table.
// `EXISTS` on the nested id rather than `IS NOT NULL` on the relation: the
// relation is indexed as `null`, which `IS NOT NULL` handles too, but the
// nested form also holds for a document indexed without the field at all.
const HAS_ORGANISER_FILTER = '(vendor.id EXISTS OR user.id EXISTS)';

// Predicate for public listings: hide what the owner ticked "inaktiv".
//
// EVERY public listing query must carry this. It is applied per query with
// nothing enforcing it, and omitting it fails silently — the hidden entries
// simply reappear, which reads to a client as "the inaktiv checkbox is broken".
// That is exactly what happened to the home page category teasers.
//
// Deliberately NOT used by the MyAccount views (Products, VendorEvents,
// VendorNews, VendorServices, UserEvents): those must list inactive entries so
// the owner can find and re-activate them.
//
// `!= true` for the same reason as the two predicates above — verified
// equivalent to `inactive = false` across all six content indexes today (no
// document is missing the field), so it also covers a future one that is.
const NOT_INACTIVE_FILTER = 'inactive != true';

// Every free-text search (site search, vendor overview, marketplace): when not
// every word matches, Meilisearch drops the most common word first, so "bio
// honig" still means honey. The default drops the last word and returned every
// bio product. See search-relevance-plan.md.
const FREE_TEXT_MATCHING_STRATEGY = MatchingStrategies.FREQUENCY;

// Default map center / fallback location for the Austrian market (Vienna, Stephansdom),
// used when no user location is known
const DEFAULT_COORDINATES = {
  latitude: 48.2082,
  longitude: 16.3738
};

const getDefaultVisibleListItemsSize = (itemLength: number) => {
  if (itemLength > DEFAULT_VISIBLE_LIST_ITEMS) {
    return DEFAULT_VISIBLE_LIST_ITEMS;
  }
  return itemLength;
};

const getDefaultMarketPlaceVisibleItemsSize = (itemLength: number) => {
  if (itemLength > MARKET_PLACE_VISIBLE_ITEMS) {
    return MARKET_PLACE_VISIBLE_ITEMS;
  }
  return itemLength;
};

export {
  getDefaultVisibleListItemsSize,
  getDefaultMarketPlaceVisibleItemsSize,
  DEFAULT_VISIBLE_LIST_ITEMS,
  MARKET_PLACE_VISIBLE_ITEMS,
  TOP_PRODUCTS_DEFAULT_VISIBLE_ITEMS,
  DEFAULT_SEARCH_DISTANCE_IN_METERS,
  BLACK_BOARD_VISIBLE,
  DEFAULT_MEILICLIENT_LIMIT,
  SITE_URL,
  ALLERGEN_INFO_URL,
  MAX_ASSET_TITLE_LENGTH,
  FOCUS_AREA_MAX_LENGTH,
  NOT_SUSPENDED_VENDOR_FILTER,
  HAS_ORGANISER_FILTER,
  NOT_DEACTIVATED_OWNER_FILTER,
  NOT_INACTIVE_FILTER,
  FREE_TEXT_MATCHING_STRATEGY,
  DEFAULT_COORDINATES,
  MINIMUM_PRICE,
  DEFAULT_FAVORITE_VENDOR_ID,
  WEBSHOP_ENABLED
};
